import React, { useEffect, useMemo, useState } from "react";
import { CalendarPlus } from "lucide-react";
import { couple, intro } from "../mock";
import useReveal from "../hooks/useReveal";

function useCountdown(targetISO) {
  const target = useMemo(() => new Date(targetISO).getTime(), [targetISO]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds };
}

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center px-4 md:px-7">
      <span className="font-serif-display text-4xl md:text-5xl font-medium text-[#211E1A] tabular-nums">
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-2 text-[11px] uppercase tracking-widest-2 text-[#6b6157]">{label}</span>
    </div>
  );
}

export default function Countdown() {
  const { days, hours, minutes, seconds } = useCountdown(couple.weddingISO);
  const [ref, visible] = useReveal();

  const addToCalendar = () => {
    const start = new Date(couple.weddingISO);
    const end = new Date(start.getTime() + 5 * 3600000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${couple.names} Wedding`,
      `LOCATION:${couple.locationShort}`,
      "DESCRIPTION:We can't wait to celebrate with you!",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "adam-and-sara-wedding.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="py-24 md:py-36 px-6">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto text-center reveal ${visible ? "is-visible" : ""}`}
      >
        <h2 className="font-serif-display text-[34px] sm:text-[44px] md:text-[54px] leading-[1.08] text-[#211E1A]">
          {intro.heading}
        </h2>
        <p className="mt-8 text-[#4a443d] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          {intro.body}
        </p>

        <div className="mt-12 flex items-center justify-center divide-x divide-[#211E1A]/15">
          <Unit value={days} label="Days" />
          <Unit value={hours} label="Hours" />
          <Unit value={minutes} label="Minutes" />
          <Unit value={seconds} label="Seconds" />
        </div>

        <button
          onClick={addToCalendar}
          className="mt-12 inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-widest-2 text-[#211E1A] border border-[#211E1A] px-7 py-[13px] rounded-sm transition-all duration-300 hover:bg-[#9E2B25] hover:border-[#9E2B25] hover:text-[#F4EFE6]"
        >
          <CalendarPlus size={16} />
          Add to Calendar
        </button>
      </div>
    </section>
  );
}
